import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { RadialScore, BarHistory, Sparkline, ConsistencyBars } from "@/components/charts";
import { DataPanel, MonoLabel, StatusBadge, StatusDot } from "@/components/primitives";
import { AGENT_PROFILE } from "@/lib/demo-data";
import { useSEO } from "@/lib/useSEO";
import { PageShell } from "@/components/PageShell";
import { cn } from "@/lib/utils";

const VERIF_TONE: Record<string, string> = {
  VERIFIED: "text-success",
  FAILED: "text-error",
  RESOLVED: "text-cyan",
  AMENDED: "text-warn",
};

export default function Reputation() {
  useSEO({
    title: "Reputation — Consequence Score | Consequence Protocol",
    description:
      "Reputation built from verified outcomes rather than raw activity. Explore the Consequence Score concept and a simulated agent profile.",
    path: "/reputation",
  });

  return (
    <PageShell testId="page-reputation">
      <section className="container-t pb-12 pt-20 sm:pt-28">
        <SectionHeader
          index="00"
          label="Reputation"
          title={
            <>
              The <span className="text-cyan glow-cyan">Consequence Score</span>
            </>
          }
          description="Reputation should be based on verified outcomes rather than raw activity. The Consequence Score projects an actor’s settled history — impact, consistency, and dispute record — into a single, evidence-backed measure."
        />
        <Reveal>
          <div className="mt-8 inline-flex items-center gap-2 border border-warn/40 bg-warn/10 px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-warn" />
            <MonoLabel className="text-[9px] text-warn">Simulated profile — demonstration data only, not protocol metrics</MonoLabel>
          </div>
        </Reveal>
      </section>

      <section className="container-t pb-24" data-testid="profile">
        <Reveal>
          <div className="panel scanlines hud-corners grid gap-10 p-6 sm:p-10 lg:grid-cols-[auto_1fr]">
            <div className="flex flex-col items-center gap-5">
              <RadialScore score={AGENT_PROFILE.score} testId="consequence-score-gauge" />
              <div className="text-center">
                <p className="font-display text-lg font-bold tracking-wide text-ink" data-testid="agent-id">
                  {AGENT_PROFILE.id}
                </p>
                <MonoLabel className="mt-1 text-[8px]">{AGENT_PROFILE.type}</MonoLabel>
              </div>
              <StatusBadge status="ACTIVE" />
            </div>
            <div className="grid content-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {AGENT_PROFILE.stats.map((s) => (
                <div key={s.label} className="border border-line/70 bg-base/60 p-5 transition-colors duration-200 hover:border-electric/40" data-testid={`profile-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <MonoLabel className="text-[8px]">{s.label}</MonoLabel>
                  <p className="mt-3 font-display text-2xl font-bold text-ink">{s.value}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-line bg-panel/40 py-24" data-testid="profile-charts">
        <div className="container-t">
          <SectionHeader index="01" label="Track Record" title="History, measured" />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            <Reveal>
              <DataPanel title="Outcome History" meta="12 EPOCHS · DEMO">
                <BarHistory data={AGENT_PROFILE.outcomeHistory} testId="chart-outcome-history" />
                <MonoLabel className="mt-3 block text-[8px]">Verified outcomes per epoch</MonoLabel>
              </DataPanel>
            </Reveal>
            <Reveal delay={0.08}>
              <DataPanel title="Success Rate" meta="12 EPOCHS · DEMO">
                <Sparkline data={AGENT_PROFILE.successRate} height={96} testId="chart-success-rate" />
                <MonoLabel className="mt-3 block text-[8px]">Verified vs settled claims (%)</MonoLabel>
              </DataPanel>
            </Reveal>
            <Reveal delay={0.16}>
              <DataPanel title="Economic Impact" meta="12 EPOCHS · DEMO">
                <Sparkline data={AGENT_PROFILE.impactSeries} stroke="#00A8FF" height={96} testId="chart-economic-impact" />
                <MonoLabel className="mt-3 block text-[8px]">Cumulative measured impact ($M)</MonoLabel>
              </DataPanel>
            </Reveal>
            <Reveal delay={0.1}>
              <DataPanel title="Consistency" meta="BY CATEGORY · DEMO" className="lg:col-span-1">
                <ConsistencyBars data={AGENT_PROFILE.consistency} />
              </DataPanel>
            </Reveal>
            <Reveal delay={0.18}>
              <DataPanel title="Verification History" meta="RECENT · DEMO" className="lg:col-span-2">
                <ul className="space-y-0">
                  {AGENT_PROFILE.verifications.map((v) => (
                    <li key={v.epoch} className="flex flex-wrap items-center justify-between gap-2 border-b border-line/60 py-3 last:border-0">
                      <span className="flex items-center gap-3">
                        <span className="font-mono text-[10px] text-dim/70">{v.epoch}</span>
                        <span className="text-sm text-dim">{v.event}</span>
                      </span>
                      <span className={cn("font-mono text-[10px] uppercase tracking-[0.18em]", VERIF_TONE[v.status])} data-testid={`verification-status-${v.epoch.toLowerCase()}`}>
                        ● {v.status}
                      </span>
                    </li>
                  ))}
                </ul>
              </DataPanel>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 flex items-center gap-3">
              <StatusDot tone="warn" label="All figures on this page are simulated demonstrations" />
            </p>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
